/**
 * Mission 35: Admin Campaign Service
 *
 * API client for admin campaign management endpoints.
 * Follows adminUserService.ts patterns exactly.
 */

import { storage } from './storage';
import { API_BASE_URL } from '../utils/api';

export class AdminCampaignService {
  private static readonly API_BASE = `${API_BASE_URL}/api/admin/campaigns`;

  private static async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Client': 'firebase-portal',
    };
    const token = await storage.getItem('admin_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private static async getFetchOptions(method: string = 'GET', body?: any): Promise<RequestInit> {
    const options: RequestInit = {
      method,
      headers: await this.getAuthHeaders(),
      credentials: 'include',
    };
    if (body) options.body = JSON.stringify(body);
    return options;
  }

  /**
   * Parse a structured error from a non-ok response.
   * Backend may return { detail: { message, error_code, ... } } or { detail: "string" }.
   */
  private static async parseError(res: Response, fallback: string): Promise<Error> {
    try {
      const body = await res.json();
      const detail = body?.detail;
      if (typeof detail === 'object' && detail?.message) {
        return Object.assign(new Error(detail.message), {
          errorCode: detail.error_code,
          detail,
        });
      }
      if (typeof detail === 'string') {
        return new Error(detail);
      }
      return new Error(body?.error || body?.message || `${fallback}: ${res.status}`);
    } catch {
      return new Error(`${fallback}: ${res.status}`);
    }
  }

  // ── Campaign CRUD ──

  static async getStats(): Promise<any> {
    const res = await fetch(`${this.API_BASE}/stats`, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to get stats');
    return res.json();
  }

  static async listCampaigns(params?: {
    status?: string; campaign_type?: string; search?: string; limit?: number; offset?: number;
  }): Promise<any> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.campaign_type) query.set('campaign_type', params.campaign_type);
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));

    const url = `${this.API_BASE}?${query.toString()}`;
    const res = await fetch(url, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to list campaigns');
    return res.json();
  }

  static async getCampaign(id: string): Promise<any> {
    const res = await fetch(`${this.API_BASE}/${id}`, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to get campaign');
    return res.json();
  }

  static async createCampaign(data: any): Promise<any> {
    const res = await fetch(`${this.API_BASE}`, await this.getFetchOptions('POST', data));
    if (!res.ok) throw await this.parseError(res, 'Failed to create campaign');
    return res.json();
  }

  static async updateCampaign(id: string, data: any): Promise<any> {
    const res = await fetch(`${this.API_BASE}/${id}`, await this.getFetchOptions('PUT', data));
    if (!res.ok) throw await this.parseError(res, 'Failed to update campaign');
    return res.json();
  }

  static async changeStatus(id: string, status: string): Promise<any> {
    const res = await fetch(
      `${this.API_BASE}/${id}/status`,
      await this.getFetchOptions('PATCH', { status }),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to change status');
    return res.json();
  }

  static async duplicateCampaign(id: string): Promise<any> {
    const res = await fetch(`${this.API_BASE}/${id}/duplicate`, await this.getFetchOptions('POST'));
    if (!res.ok) throw await this.parseError(res, 'Failed to duplicate campaign');
    return res.json();
  }

  static async fireManualStep(campaignId: string, stepId: string): Promise<any> {
    const res = await fetch(
      `${this.API_BASE}/${campaignId}/steps/${stepId}/fire`,
      await this.getFetchOptions('POST'),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to fire step');
    return res.json();
  }

  // ── Analytics ──

  static async getAnalytics(id: string): Promise<any> {
    const res = await fetch(`${this.API_BASE}/${id}/analytics`, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to get analytics');
    return res.json();
  }

  // ── Triggers ──

  static async getTriggers(): Promise<any[]> {
    const res = await fetch(`${this.API_BASE}/triggers`, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to get triggers');
    return res.json();
  }

  // ── Audience ──

  static async getAudienceCount(filters: any[]): Promise<{ count: number; total_users: number }> {
    const res = await fetch(
      `${this.API_BASE}/audience-count`,
      await this.getFetchOptions('POST', { filters }),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to count audience');
    return res.json();
  }

  // ── Survey Results ──

  static async getSurveyResults(campaignId: string, params?: { limit?: number; offset?: number }): Promise<any> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));

    const res = await fetch(
      `${this.API_BASE}/${campaignId}/survey-results?${query.toString()}`,
      await this.getFetchOptions(),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to get survey results');
    return res.json();
  }

  static async exportSurveyResults(campaignId: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    const res = await fetch(
      `${this.API_BASE}/${campaignId}/survey-results/export?format=${format}`,
      await this.getFetchOptions(),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to export survey results');
    return res.blob();
  }

  // ── Segments ──

  static async listSegments(): Promise<any[]> {
    const res = await fetch(`${this.API_BASE}/segments/list`, await this.getFetchOptions());
    if (!res.ok) throw await this.parseError(res, 'Failed to list segments');
    return res.json();
  }

  static async createSegment(data: { name: string; description?: string; filters: any[] }): Promise<any> {
    const res = await fetch(`${this.API_BASE}/segments`, await this.getFetchOptions('POST', data));
    if (!res.ok) throw await this.parseError(res, 'Failed to create segment');
    return res.json();
  }

  static async updateSegment(id: string, data: any): Promise<any> {
    const res = await fetch(`${this.API_BASE}/segments/${id}`, await this.getFetchOptions('PUT', data));
    if (!res.ok) throw await this.parseError(res, 'Failed to update segment');
    return res.json();
  }

  static async deleteSegment(id: string): Promise<any> {
    const res = await fetch(`${this.API_BASE}/segments/${id}`, await this.getFetchOptions('DELETE'));
    if (!res.ok) throw await this.parseError(res, 'Failed to delete segment');
    return res.json();
  }

  // ── User picker (for specific_users targeting) ──

  static async getActiveUsers(): Promise<Array<{ id: string; email: string; firstName: string; lastName: string; status: string }>> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/users/all?include_status=active`,
      await this.getFetchOptions(),
    );
    if (!res.ok) throw await this.parseError(res, 'Failed to fetch active users');
    const data = await res.json();
    return data.users || [];
  }
}
